import React, { useState } from 'react';
import { Query, withApollo } from 'react-apollo';
import { Alert, Avatar, Button, Empty, Input, Modal, Pagination, Select, Spin, Table, Tag, message } from 'antd';
import UserDetail from '../../components/UserDetail';
import { GET_CURRENT_USER } from '../../api/user';
import { GET_CHAT_REPORTS, GET_CHAT_REPORT, GET_CHAT_REPORT_MESSAGES, REVIEW_CHAT_REPORT } from '../../api/chatReport';
import './index.css';

const labels = { pending: '待处理', handled: '已处理', ignored: '已忽略' };
const time = value => value ? new Date(value).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false }) : '未记录';
export const mediaUrl = value => {
  if (!value || typeof value !== 'string') return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (/^[a-z][a-z\d+.-]*:/i.test(value) || value.startsWith('//')) return null;
  return `https://cdn.zhitiaox.com/${value.replace(/^\/+/, '')}`;
};
const errorView = (error, refetch) => <Alert type="error" showIcon message="加载失败" description={<span>{error.message} <Button onClick={() => refetch()}>重试</Button></span>} />;
const statusTag = value => <Tag color={value === 'pending' ? 'orange' : 'green'}>{labels[value] || value}</Tag>;

function Participant({ person, onView }) {
  return <div className="chat-report-person">
    <Avatar src={mediaUrl(person.avatarUrl)} />
    <div><b>{person.role === 'reporter' ? '举报人' : '被举报人'}</b> · {person.nickname || '未记录昵称'}
      <div className="chat-report-id">UID：{person.uid}</div>
      {person.isAnom !== null && <span>聊天身份：{person.isAnom ? '匿名' : '实名'}　</span>}
      {person.missing ? <Tag>用户已不存在</Tag> : <Button size="small" onClick={() => onView(person.uid)}>查看用户详情</Button>}
    </div>
  </div>;
}
function Messages({ id, onView }) {
  const [page, setPage] = useState(1);
  return <Query query={GET_CHAT_REPORT_MESSAGES} variables={{ id, page }} fetchPolicy="network-only">
    {({ loading, error, data, refetch }) => {
      if (loading) return <Spin />;
      if (error) return errorView(error, refetch);
      const { items, total } = data.chatReportMessages;
      return <div>
        {!items.length && <Empty description="暂无可关联的聊天记录" />}
        {items.map(item => <article className="chat-report-message" key={item._id}>
          <div><Button size="small" disabled={!item.senderId} onClick={() => onView(item.senderId)}>发送者：{item.senderId || '未知'}</Button> <span>{time(item.createdAt)}</span></div>
          <div className="chat-report-text">{item.content}</div>
          {mediaUrl(item.imgUrl) && <a href={mediaUrl(item.imgUrl)} target="_blank" rel="noopener noreferrer"><img src={mediaUrl(item.imgUrl)} alt="聊天图片，点击查看原图" /></a>}
          {mediaUrl(item.videoUrl) && <video src={mediaUrl(item.videoUrl)} controls preload="metadata" />}
        </article>)}
        <Pagination current={page} pageSize={50} total={total} onChange={setPage} />
      </div>;
    }}
  </Query>;
}
function DetailContent({ report, client, refetch, onView }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const review = async status => {
    if (!note.trim()) { message.warning('请填写处理说明'); return; }
    setSaving(true);
    try {
      await client.mutate({ mutation: REVIEW_CHAT_REPORT, variables: { id: report._id, status, note } });
      message.success('举报处理结果已保存');
      setNote('');
      try { await refetch(); } catch (error) { message.warning('处理已成功，详情刷新失败，请重新打开'); }
    } catch (error) {
      message.error(error.message || '处理失败');
      // A concurrent review may have won. Read its result instead of overwriting it.
      try { await refetch(); } catch (ignored) { /* Retry remains available in the query error UI. */ }
    } finally { setSaving(false); }
  };
  return <div>
    <section className="chat-report-section">
      <h3>举报信息 {statusTag(report.status)}</h3>
      <div>举报编号：{report._id}</div><div>聊天室 ID：{report.roomId || '未记录'}</div>
      <div>举报时间：{time(report.createdAt)}{report.timeEstimated && '（根据举报 ID 推算）'}</div>
      <div>举报类型：{report.reason || '未记录'}</div>
      <div className="chat-report-text">备注：{report.content || '未填写'}</div>
      <div className="chat-report-images">{(report.images || []).map((url, index) => mediaUrl(url) && <a key={index} href={mediaUrl(url)} target="_blank" rel="noopener noreferrer"><img src={mediaUrl(url)} alt={`举报截图 ${index + 1}，点击查看原图`} /></a>)}</div>
    </section>
    <section className="chat-report-section"><h3>对话人员</h3>
      {report.roomMissing && <Alert type="warning" message="聊天室不存在，无法完整关联对话人员。" />}
      {(report.participants || []).map(person => <Participant key={person.uid} person={person} onView={onView} />)}
    </section>
    <section className="chat-report-section"><h3>处理举报</h3>
      {report.status === 'pending' ? <div>
        <p>请记录核查结论及已采取的措施。保存结果只更新举报状态，不会自动处罚用户。</p>
        <Input.TextArea value={note} onChange={e => setNote(e.target.value)} maxLength={1000} rows={3} placeholder="填写处理说明或忽略原因（必填，最多1000字）" disabled={saving} />
        <div className="chat-report-actions"><Button type="primary" loading={saving} disabled={saving || !note.trim()} onClick={() => review('handled')}>标记已处理</Button>
          <Button disabled={saving || !note.trim()} onClick={() => review('ignored')}>忽略举报</Button></div>
      </div> : <div><div>处理人：{report.reviewedBy || '未记录'}　处理时间：{time(report.reviewedAt)}</div><div className="chat-report-text">处理说明：{report.reviewNote || '未记录'}</div></div>}
    </section>
    <section className="chat-report-section"><h3>聊天记录</h3>
      <Alert type="info" showIcon message="历史关联记录，非举报时快照" description="以下为当前留存的聊天内容，可能包含举报后的消息；举报人删除聊天框不会清除这些记录。" />
      <Messages key={report._id} id={report._id} onView={onView} />
    </section>
  </div>;
}
function ChatReports({ client, history, match }) {
  const id = match.params.id;
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('pending');
  const [reporter, setReporter] = useState('');
  const [room, setRoom] = useState('');
  const [filter, setFilter] = useState({});
  const [userId, setUserId] = useState('');
  const apply = () => {
    if ([reporter, room].some(value => value.trim() && !/^[a-f\d]{24}$/i.test(value.trim()))) { message.warning('请输入24位 UID 或聊天室 ID'); return; }
    setFilter({ reporterId: reporter.trim() || null, roomId: room.trim() || null }); setPage(1);
  };
  return <div className="chat-reports">
    <h2>聊天举报</h2>
    {id ? <div><Button onClick={() => history.push('/chat-reports')}>返回举报列表</Button>
      <Query query={GET_CHAT_REPORT} variables={{ id }} fetchPolicy="network-only">
        {({ loading, error, data, refetch }) => loading ? <Spin /> : error ? errorView(error, refetch) :
          <DetailContent key={id} report={data.chatReport} client={client} refetch={refetch} onView={setUserId} />}
      </Query></div> : <div>
      <div className="chat-report-filters">
        <Select value={status} onChange={value => { setStatus(value); setPage(1); }} style={{ width: 130 }}>
          <Select.Option value="pending">待处理</Select.Option><Select.Option value="handled">已处理</Select.Option>
          <Select.Option value="ignored">已忽略</Select.Option><Select.Option value="all">全部状态</Select.Option>
        </Select>
        <Input placeholder="举报人 UID" value={reporter} onChange={e => setReporter(e.target.value)} onPressEnter={apply} />
        <Input placeholder="聊天室 ID" value={room} onChange={e => setRoom(e.target.value)} onPressEnter={apply} />
        <Button type="primary" onClick={apply}>查询</Button>
      </div>
      <Query query={GET_CHAT_REPORTS} variables={{ page, status, ...filter }} fetchPolicy="network-only">
        {({ loading, error, data, refetch }) => {
          if (error) return errorView(error, refetch);
          const result = (data && data.chatReports) || { items: [], total: 0 };
          return <div><Button size="small" onClick={() => refetch()} disabled={loading}>刷新</Button>
            <Table loading={loading} rowKey="_id" dataSource={result.items} pagination={false} scroll={{ x: 1000 }} columns={[
              { title: '举报时间', key: 'time', render: (_, r) => <span>{time(r.createdAt)}{r.timeEstimated && <small>（推算）</small>}</span> },
              { title: '对话人员', key: 'people', render: (_, r) => <div>{(r.participants || []).map(p => <div key={p.uid}>{p.role === 'reporter' ? '举报人' : '被举报人'}：<Button type="link" disabled={p.missing} onClick={() => setUserId(p.uid)}>{p.nickname || p.uid}</Button><div className="chat-report-id">{p.uid}</div></div>)}{r.roomMissing && <Tag>聊天室不存在</Tag>}</div> },
              { title: '举报类型', dataIndex: 'reason', render: value => value || '未记录' },
              { title: '备注', dataIndex: 'content', render: value => <span title={value}>{value ? value.slice(0, 80) : '未填写'}</span> },
              { title: '状态', dataIndex: 'status', render: statusTag },
              { title: '操作', key: 'action', render: (_, r) => <Button onClick={() => history.push(`/chat-reports/${r._id}`)}>查看详情</Button> },
            ]} />
            <Pagination current={page} total={result.total} pageSize={20} onChange={setPage} showTotal={total => `共 ${total} 条`} />
          </div>;
        }}
      </Query>
    </div>}
    <Modal title={`用户详情 · ${userId}`} visible={!!userId} onCancel={() => setUserId('')} footer={null} width={850} destroyOnClose>
      {userId && <UserDetail _id={userId} type="View" />}
    </Modal>
  </div>;
}
const Connected = withApollo(ChatReports);
export default function ChatReportPage(props) {
  return <Query query={GET_CURRENT_USER}>{({ loading, error, data }) => {
    if (loading) return <Spin />;
    if (error || !data || !data.currentUser || data.currentUser.role !== 'admin') return <Alert type="error" message="仅管理员可访问聊天举报" />;
    return <Connected {...props} />;
  }}</Query>;
}
